import type { Group } from '../../platform/primitives/DataGrid'
import { totalsByCurrency } from '../../platform/money/totals'
import type { Txn } from './transactionWire'

/**
 * Rows → day bands, each with one subtotal per currency (D21).
 *
 * The subtotal of a band is `totalsByCurrency` of exactly that band's rows, so
 * it reconciles with them by construction; the grid asserts it anyway.
 *
 * Bands are runs of equal `date` in server order (`transaction_date DESC`), so
 * the design's own failing render — one band absorbing the previous day's rows
 * into its total — cannot be produced: a different date starts a new band.
 */
export function dayGroups(
  rows: readonly Txn[],
  opts: { continuesFrom?: string; locale?: string },
): Group<Txn>[] {
  const locale = opts.locale ?? 'en-GB'
  const groups: Group<Txn>[] = []
  let current: { date: string; rows: Txn[] } | undefined
  const runs: { date: string; rows: Txn[] }[] = []
  for (const row of rows) {
    if (current && current.date === row.date) {
      current.rows.push(row)
    } else {
      current = { date: row.date, rows: [row] }
      runs.push(current)
    }
  }
  runs.forEach((run, i) => {
    const continued = i === 0 && opts.continuesFrom !== undefined && opts.continuesFrom === run.date
    groups.push({
      label: dayLabel(run.date, locale) + (continued ? ' (continued)' : ''),
      subtotals: totalsByCurrency(run.rows, (r) => r.amountCents, (r) => r.currency),
      rows: run.rows,
    })
  })
  return groups
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * "Tuesday, 28 July". The date is read as a calendar date: it is built at UTC
 * midnight and formatted in UTC, so no local offset can move it a day — a
 * viewer in Vancouver sees the 28th as the 28th.
 */
function dayLabel(date: string, locale: string): string {
  const m = DATE.exec(date)
  if (!m) throw new Error(`dayGroups: not a YYYY-MM-DD date: ${JSON.stringify(date)}`)
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  const part = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).format(at)
  return `${part({ weekday: 'long' })}, ${part({ day: 'numeric' })} ${part({ month: 'long' })}`
}
